/**
 * Las reglas de leer un código de barras con la cámara.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTO ESTÁ APARTE DE LA PANTALLA
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Porque la cámara no se puede probar y estas reglas sí. Un navegador de mentira
 * no tiene cámara, así que la pantalla del escáner solo se puede mirar a ojo;
 * pero decidir **cuándo una lectura vale y cuándo hay que ignorarla** es lógica
 * pura, y ahí es donde están los fallos que no se ven.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  EL PROBLEMA QUE NADIE ESPERA: LA CÁMARA LEE UNA Y OTRA VEZ
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Un lector láser dispara una vez por producto: se pasa, pita, se acabó. Una
 * cámara no: mientras el código esté delante lo lee **una y otra vez**, cada
 * `INTERVALO_DE_LECTURA`.
 *
 * Sin freno, apuntar dos segundos a una harina mete diez harinas al carrito.
 * Y el cajero no lo ve venir, porque en su cabeza «pasó el producto una vez».
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL MISMO CÓDIGO CUENTA OTRA VEZ CUANDO SALIÓ DE LA CÁMARA, NO AL RATO
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Antes la regla era «el mismo código, dos segundos después de aceptarlo». El
 * dueño vio que «un día no sumó», y fallaba por los dos lados:
 *
 *   · **Dos panes iguales pasados seguido** —en menos de dos segundos— contaban
 *     uno. El segundo se tragaba sin aviso.
 *   · **Un pan quieto delante de la cámara** más de dos segundos contaba dos, y
 *     tres a los cuatro. Esa ni se veía.
 *
 * Lo que distingue «otro pan» de «el mismo pan que sigue ahí» no es el reloj:
 * es que el código **desaparezca** de la cámara entre uno y otro. Así que se
 * apunta cuándo se VIO por última vez, y el mismo código vuelve a contar solo
 * tras `AUSENCIA_PARA_REPETIR` sin verse.
 *
 * Otro código distinto tiene su propia espera, corta, para que pasar la compra
 * sea rápido sin que un cuadro borroso cuele una lectura a medias.
 */

/**
 * Cuánto tiene que DESAPARECER un código de la cámara para que vuelva a contar.
 *
 * Son cuatro intentos de lectura seguidos sin verlo: un cuadro borroso suelto
 * con el pan quieto delante no llega, y apartar un pan para acercar el
 * siguiente, sí.
 */
export const AUSENCIA_PARA_REPETIR = 800;

/** Cuánto se ignora cualquier OTRO código después de una lectura buena. */
export const ESPERA_CUALQUIERA = 600;

/**
 * Cada cuánto intenta leer la cámara.
 *
 * ⚠️ La librería trae 500 ms de fábrica: con eso un código quieto se ve solo
 * dos veces por segundo, y un cuadro fallido dejaba un hueco de un segundo que
 * se confundía con «lo apartó». Con 200 ms, cinco lecturas por segundo, que el
 * teléfono de una bodega aguanta porque solo se prueban los `FORMATOS`.
 */
export const INTERVALO_DE_LECTURA = 200;

/** Lo último que se aceptó, y cuándo se vio por última vez. */
export type UltimaLectura = { codigo: string; cuando: number; visto: number };

/**
 * Los formatos que se leen, y ninguno más.
 *
 * ⚠️ La lista corta no es por gusto: cuantos menos formatos, menos trabajo por
 * cuadro, y eso en el teléfono de 60 dólares de una bodega es la diferencia
 * entre leer al instante y parecer que se colgó.
 *
 * Están los del comercio —EAN y UPC son los de cualquier producto de estante,
 * CODE_128 y CODE_39 los de las etiquetas que imprime uno mismo, ITF el de las
 * cajas de mayor— y el QR, porque hay negocios que etiquetan así.
 */
export const FORMATOS = [
  "EAN_13",
  "EAN_8",
  "UPC_A",
  "UPC_E",
  "CODE_128",
  "CODE_39",
  "ITF",
  "QR_CODE",
] as const;

export type FormatoDeCodigo = (typeof FORMATOS)[number];

/**
 * ¿Esta lectura vale, o es la misma de hace un instante?
 *
 * Se le pasa lo último que se aceptó y el reloj de ahora. Devuelve `true` solo
 * cuando de verdad hay algo nuevo que hacer.
 */
export function valeLaLectura(
  codigo: string,
  ultimo: UltimaLectura | null,
  ahora: number,
): boolean {
  if (!codigo.trim()) return false;
  if (!ultimo) return true;

  // El mismo: solo si dejó de verse un rato. Ver el encabezado.
  if (codigo === ultimo.codigo) return ahora - ultimo.visto >= AUSENCIA_PARA_REPETIR;
  return ahora - ultimo.cuando >= ESPERA_CUALQUIERA;
}

/**
 * Lo que queda apuntado después de ver un código, valga o no.
 *
 * ⚠️ Aunque no valga, si es el mismo que ya se aceptó se apunta que SIGUE
 * delante: es lo que impide que un pan quieto vuelva a contar.
 */
export function trasVer(
  codigo: string,
  ultimo: UltimaLectura | null,
  ahora: number,
): UltimaLectura | null {
  if (valeLaLectura(codigo, ultimo, ahora)) return { codigo, cuando: ahora, visto: ahora };
  if (ultimo && codigo === ultimo.codigo) return { ...ultimo, visto: ahora };
  return ultimo;
}

/**
 * Limpia lo que devolvió la cámara: **solo los bordes**.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ NO SE QUITAN LOS ESPACIOS NI LOS GUIONES DE DENTRO
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * La primera versión los quitaba todos, y estaba mal. Lo destapó su propia
 * prueba, y el motivo es que **hay formatos donde el guion es parte del dato**:
 *
 *   · EAN y UPC —los de cualquier producto de estante— son solo dígitos, así
 *     que ahí daba igual.
 *   · Pero CODE_39 y CODE_128 son los de las etiquetas que imprime el propio
 *     negocio, y ahí caben letras, guiones y espacios. Un SKU «CAM-M-NEG»
 *     quedaba convertido en «CAMMNEG» y no encontraba nada.
 *
 * Y el fallo no se veía: el producto «no aparece», así que alguien lo carga otra
 * vez y el inventario acaba con dos.
 *
 * Recortar los bordes sí hace falta: según el formato llega con un salto de
 * línea detrás, y un código con un carácter invisible al final tampoco encuentra
 * nada, con la misma consecuencia.
 */
export function limpiarCodigo(crudo: string): string {
  return crudo.trim();
}

/**
 * Qué decirle a la persona cuando la cámara no arranca.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL MENSAJE TIENE QUE DECIR QUÉ HACER, NO QUÉ FALLÓ
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * «NotAllowedError» no le sirve a nadie. Los tres casos que pasan de verdad
 * tienen los tres una salida distinta, y esa salida es lo único que importa:
 *
 *   · **No dio permiso** (o lo negó antes sin darse cuenta) → hay que dárselo
 *     en el candado de la barra de direcciones. Es el más común con diferencia y
 *     el que más gente da por perdido, porque el navegador no vuelve a
 *     preguntar.
 *   · **La cámara está ocupada** por otra aplicación — la videollamada, otra
 *     pestaña de Softfi abierta.
 *   · **No hay cámara**, que en una computadora de escritorio es lo normal.
 *
 * Y hay un cuarto que no es un fallo del aparato: **sin candado no hay cámara**.
 * Los navegadores solo la dan por HTTPS. Softfi va por HTTPS, así que esto solo
 * salta abriendo por la dirección de la red local.
 */
export function porQueNoAbrio(error: unknown): string {
  const nombre =
    error && typeof error === "object" && "name" in error ? String(error.name) : "";

  if (nombre === "NotAllowedError" || nombre === "SecurityError") {
    return "No diste permiso para la cámara. Tócale al candado de la barra de arriba y permite la cámara para Softfi.";
  }
  if (nombre === "NotFoundError" || nombre === "OverconstrainedError") {
    return "Este aparato no tiene cámara. Escribe el código a mano o usa un lector.";
  }
  if (nombre === "NotReadableError" || nombre === "AbortError") {
    return "La cámara la está usando otra aplicación. Ciérrala y vuelve a intentar.";
  }
  return "No se pudo abrir la cámara. Escribe el código a mano mientras tanto.";
}

/** ¿Este navegador puede dar la cámara? */
export function hayCamara(): boolean {
  return Boolean(navigator.mediaDevices?.getUserMedia);
}

/**
 * ¿Aquí se escanea con la cámara? Solo en el teléfono (y en la tablet).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ «PUEDE DAR CÁMARA» NO ES «AQUÍ SE ESCANEA CON LA CÁMARA»
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El botón de la cámara salía también en la computadora, porque `hayCamara`
 * solo pregunta si el navegador SABE pedir cámara — y en una PC la respuesta es
 * sí, tenga webcam o no. Lo vio el dueño en su pantalla: una cámara en cada
 * buscador del mostrador, donde hay un lector láser o se teclea.
 *
 * Y aunque la PC tenga webcam, no sirve para esto: apunta a la cara de quien
 * está sentado, no al producto. Escanear con la cámara es cosa del teléfono,
 * que se lleva a la estantería o se apunta al código con la mano.
 *
 * La pregunta de verdad es si el puntero PRINCIPAL es un dedo (`pointer:
 * coarse`): teléfono o tablet. Una portátil con pantalla táctil sigue teniendo
 * el ratón como principal, y se queda sin cámara, como la PC.
 */
export function seEscaneaConLaCamara(): boolean {
  if (!hayCamara()) return false;
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/**
 * ¿Lleva botón de escáner la casilla? Casi siempre sí (2026-11-02).
 *
 * En el teléfono y la tablet es la cámara; en la PC, el TELÉFONO unido a esa
 * PC por un QR (ver `lib/telefonoDeLaPc.ts`). Solo se queda sin botón la
 * tablet sin cámara.
 *
 * ⚠️ Es lo que tienen que preguntar los buscadores para dejarle sitio al botón
 * dentro de la casilla —no `seEscaneaConLaCamara`, que en la PC dice que no y
 * dejaría el teléfono encima del texto—.
 */
export function hayBotonDeEscaner(): boolean {
  if (seEscaneaConLaCamara()) return true;
  try {
    return !window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return true;
  }
}
